/**
 * Phase 20 Plan 20-02 — API client for notification + alert endpoints.
 *
 * Covers:
 *   - 5 alert endpoints (Phase 19-02)
 *   - 13 notification-settings endpoints (Plan 20-01)
 *
 * Pattern mirrors services/kpis.ts: each function throws ApiError on non-2xx.
 */

import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  AddPhoneRequest,
  AlertDto,
  AlertListQuery,
  CreateRecipientRequest,
  NotificationTypeInfoDto,
  PhoneNumberDto,
  PaginatedResult,
  RecipientDetailDto,
  RecipientListItemDto,
  SubscriptionDto,
  UnreadCountDto,
  UpdatePhoneRequest,
  UpdateRecipientRequest,
  UpdateSubscriptionsRequest,
} from '@/types/notifications';

// ---------------------------------------------------------------------------
// Alert endpoints (Phase 19-02)
// ---------------------------------------------------------------------------

/** `GET /api/alerts?status=&severity=&from=&to=&page=&pageSize=` */
export async function getAlertsRequest(query: AlertListQuery): Promise<PaginatedResult<AlertDto>> {
  try {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.severity) params.set('severity', query.severity);
    if (query.notificationType) params.set('notificationType', query.notificationType);
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    if (query.page !== undefined) params.set('page', String(query.page));
    if (query.pageSize !== undefined) params.set('pageSize', String(query.pageSize));
    const { data } = await api.get<PaginatedResult<AlertDto>>(`/api/alerts?${params.toString()}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `GET /api/alerts/{id}` */
export async function getAlertDetailRequest(id: number): Promise<AlertDto> {
  try {
    const { data } = await api.get<AlertDto>(`/api/alerts/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/alerts/{id}/acknowledge` */
export async function acknowledgeAlertRequest(id: number): Promise<AlertDto> {
  try {
    const { data } = await api.post<AlertDto>(`/api/alerts/${String(id)}/acknowledge`, null);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/alerts/{id}/dismiss` */
export async function dismissAlertRequest(id: number): Promise<AlertDto> {
  try {
    const { data } = await api.post<AlertDto>(`/api/alerts/${String(id)}/dismiss`, null);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `GET /api/alerts/unread-count` — returns `{ count: number }` */
export async function getAlertsUnreadCountRequest(): Promise<UnreadCountDto> {
  try {
    const { data } = await api.get<UnreadCountDto>('/api/alerts/unread-count');
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

// ---------------------------------------------------------------------------
// Notification settings endpoints (Plan 20-01)
// ---------------------------------------------------------------------------

/** `GET /api/notifications/types` */
export async function getNotificationTypesRequest(): Promise<NotificationTypeInfoDto[]> {
  try {
    const { data } = await api.get<NotificationTypeInfoDto[]>('/api/notifications/types');
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `GET /api/notifications/recipients?includeInactive=&page=&pageSize=` */
export async function getRecipientsRequest(params: {
  readonly includeInactive?: boolean;
  readonly page?: number;
  readonly pageSize?: number;
}): Promise<PaginatedResult<RecipientListItemDto>> {
  try {
    const qp = new URLSearchParams();
    if (params.includeInactive !== undefined) qp.set('includeInactive', String(params.includeInactive));
    if (params.page !== undefined) qp.set('page', String(params.page));
    if (params.pageSize !== undefined) qp.set('pageSize', String(params.pageSize));
    const { data } = await api.get<PaginatedResult<RecipientListItemDto>>(
      `/api/notifications/recipients?${qp.toString()}`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `GET /api/notifications/recipients/{id}` */
export async function getRecipientDetailRequest(id: number): Promise<RecipientDetailDto> {
  try {
    const { data } = await api.get<RecipientDetailDto>(`/api/notifications/recipients/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/notifications/recipients` — 201 RecipientDetailDto */
export async function createRecipientRequest(body: CreateRecipientRequest): Promise<RecipientDetailDto> {
  try {
    const { data } = await api.post<RecipientDetailDto>('/api/notifications/recipients', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `PUT /api/notifications/recipients/{id}` */
export async function updateRecipientRequest(
  id: number,
  body: UpdateRecipientRequest,
): Promise<RecipientDetailDto> {
  try {
    const { data } = await api.put<RecipientDetailDto>(
      `/api/notifications/recipients/${String(id)}`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/notifications/recipients/{id}/deactivate` */
export async function deactivateRecipientRequest(id: number): Promise<RecipientDetailDto> {
  try {
    const { data } = await api.post<RecipientDetailDto>(
      `/api/notifications/recipients/${String(id)}/deactivate`,
      null,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/notifications/recipients/{id}/reactivate` */
export async function reactivateRecipientRequest(id: number): Promise<RecipientDetailDto> {
  try {
    const { data } = await api.post<RecipientDetailDto>(
      `/api/notifications/recipients/${String(id)}/reactivate`,
      null,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `GET /api/notifications/recipients/{id}/phones` */
export async function getRecipientPhonesRequest(recipientId: number): Promise<PhoneNumberDto[]> {
  try {
    const { data } = await api.get<PhoneNumberDto[]>(
      `/api/notifications/recipients/${String(recipientId)}/phones`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/notifications/recipients/{id}/phones` — 201 PhoneNumberDto */
export async function addPhoneRequest(
  recipientId: number,
  body: AddPhoneRequest,
): Promise<PhoneNumberDto> {
  try {
    const { data } = await api.post<PhoneNumberDto>(
      `/api/notifications/recipients/${String(recipientId)}/phones`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `PUT /api/notifications/recipients/{id}/phones/{phoneId}` */
export async function updatePhoneRequest(
  recipientId: number,
  phoneId: number,
  body: UpdatePhoneRequest,
): Promise<PhoneNumberDto> {
  try {
    const { data } = await api.put<PhoneNumberDto>(
      `/api/notifications/recipients/${String(recipientId)}/phones/${String(phoneId)}`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `DELETE /api/notifications/recipients/{id}/phones/{phoneId}` — 204 no body */
export async function deletePhoneRequest(recipientId: number, phoneId: number): Promise<void> {
  try {
    await api.delete(
      `/api/notifications/recipients/${String(recipientId)}/phones/${String(phoneId)}`,
    );
  } catch (e) {
    throw toApiError(e);
  }
}

/** `GET /api/notifications/recipients/{id}/subscriptions` */
export async function getSubscriptionsRequest(recipientId: number): Promise<SubscriptionDto[]> {
  try {
    const { data } = await api.get<SubscriptionDto[]>(
      `/api/notifications/recipients/${String(recipientId)}/subscriptions`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `PUT /api/notifications/recipients/{id}/subscriptions` */
export async function updateSubscriptionsRequest(
  recipientId: number,
  body: UpdateSubscriptionsRequest,
): Promise<SubscriptionDto[]> {
  try {
    const { data } = await api.put<SubscriptionDto[]>(
      `/api/notifications/recipients/${String(recipientId)}/subscriptions`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}
